import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { createTour, updateTour } from "@/lib/toursApi";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import { formatErrorMessage } from "@/lib/errorMessages";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  tour_code: z.string().min(1, "Tour code is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  type: z.enum(["driver", "guide", "staff", "safari"]),
  pricing_type: z.enum(["per_person", "per_family", "per_group"]),
  max_capacity: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseInt(val);
    return !isNaN(num) && num >= 0;
  }, "Max capacity must be greater than or equal to 0"),
  image_url: z.string().optional(),
  duration_hours: z.string().min(1, "Duration is required").refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Duration must be greater than or equal to 0"),
  price_op: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price OP must be greater than or equal to 0"),
  price_ag: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price AG must be greater than or equal to 0"),
  price_cf: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price CF must be greater than or equal to 0"),
  supplier_name: z.string().optional(),
  cost_supplier: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Cost Supplier must be greater than or equal to 0"),
  cost_supplier_extra_hour: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Cost Supplier Extra Hour must be greater than or equal to 0"),
  price_extra_hour_op: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price Extra Hour OP must be greater than or equal to 0"),
  price_extra_hour_ag: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price Extra Hour AG must be greater than or equal to 0"),
  price_extra_hour_cf: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price Extra Hour CF must be greater than or equal to 0"),
  tax_percentage: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Tax percentage must be greater than or equal to 0"),
  hcte_notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface TourFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
}

const TourForm = ({ open, onOpenChange, initialData, onSuccess }: TourFormProps) => {
  const { toast } = useToast();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tour_code: "",
      title: "",
      description: "",
      type: "driver",
      pricing_type: "per_person",
      max_capacity: "",
      image_url: "",
      duration_hours: "",
      price_op: "",
      price_ag: "",
      price_cf: "",
      supplier_name: "",
      cost_supplier: "",
      cost_supplier_extra_hour: "",
      price_extra_hour_op: "",
      price_extra_hour_ag: "",
      price_extra_hour_cf: "",
      tax_percentage: "",
      hcte_notes: "",
    },
  });

  const {
    handleOpenChange: handleUnsavedOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  } = useUnsavedChanges({
    isDirty: form.formState.isDirty,
    onSave: () => form.handleSubmit(onSubmit)(),
    onDiscard: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (pendingClose) {
      onOpenChange(false);
      setPendingClose(false);
    }
  }, [pendingClose, onOpenChange, setPendingClose]);

  useEffect(() => {
    if (initialData) {
      form.reset({
        tour_code: initialData.tour_code || "",
        title: initialData.title || "",
        description: initialData.description || "",
        type: initialData.type || "driver",
        pricing_type: initialData.pricing_type || "per_person",
        max_capacity: initialData.max_capacity?.toString() || "",
        image_url: initialData.image_url || "",
        duration_hours: initialData.duration_hours?.toString() || "",
        price_op: initialData.price_op?.toString() || "",
        price_ag: initialData.price_ag?.toString() || "",
        price_cf: initialData.price_cf?.toString() || "",
        supplier_name: initialData.supplier_name || "",
        cost_supplier: initialData.cost_supplier?.toString() || "",
        cost_supplier_extra_hour: initialData.cost_supplier_extra_hour?.toString() || "",
        price_extra_hour_op: initialData.price_extra_hour_op?.toString() || "",
        price_extra_hour_ag: initialData.price_extra_hour_ag?.toString() || "",
        price_extra_hour_cf: initialData.price_extra_hour_cf?.toString() || "",
        tax_percentage: initialData.tax_percentage?.toString() || "",
        hcte_notes: initialData.hcte_notes || "",
      });
    } else {
      form.reset();
    }
  }, [initialData, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        tour_code: data.tour_code,
        title: data.title,
        description: data.description || null,
        type: data.type,
        pricing_type: data.pricing_type,
        max_capacity: data.max_capacity ? parseInt(data.max_capacity) : null,
        image_url: data.image_url || null,
        duration_hours: parseFloat(data.duration_hours),
        price_op: data.price_op ? parseFloat(data.price_op) : null,
        price_ag: data.price_ag ? parseFloat(data.price_ag) : null,
        price_cf: data.price_cf ? parseFloat(data.price_cf) : null,
        supplier_name: data.supplier_name || null,
        cost_supplier: data.cost_supplier ? parseFloat(data.cost_supplier) : null,
        cost_supplier_extra_hour: data.cost_supplier_extra_hour ? parseFloat(data.cost_supplier_extra_hour) : null,
        price_extra_hour_op: data.price_extra_hour_op ? parseFloat(data.price_extra_hour_op) : null,
        price_extra_hour_ag: data.price_extra_hour_ag ? parseFloat(data.price_extra_hour_ag) : null,
        price_extra_hour_cf: data.price_extra_hour_cf ? parseFloat(data.price_extra_hour_cf) : null,
        tax_percentage: data.tax_percentage ? parseFloat(data.tax_percentage) : null,
        hcte_notes: data.hcte_notes || null,
      };

      if (initialData?.id) {
        await updateTour(initialData.id, payload);
        toast({ title: "Success", description: "Tour updated successfully" });
      } else {
        await createTour(payload);
        toast({ title: "Success", description: "Tour created successfully" });
      }

      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: formatErrorMessage(error, "Failed to save tour"),
        variant: "destructive",
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      const shouldClose = handleUnsavedOpenChange(open);
      if (shouldClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Edit Tour" : "Add New Tour"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tour_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tour Code *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="driver">Driver</SelectItem>
                        <SelectItem value="guide">Guide</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="safari">Safari</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pricing_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pricing Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="per_person">Per person</SelectItem>
                        <SelectItem value="per_family">Per family</SelectItem>
                        <SelectItem value="per_group">Per group</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="max_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Capacity</FormLabel>
                    <FormControl>
                      <NumericInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        allowDecimals={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="duration_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (Hours) *</FormLabel>
                    <FormControl>
                      <NumericInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        allowDecimals={true}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="price_op"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price OP (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_ag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price AG (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_cf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price CF (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost_supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost Supplier (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="cost_supplier_extra_hour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost Supplier Extra Hour (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_extra_hour_op"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price Extra Hour OP (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_extra_hour_ag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price Extra Hour AG (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_extra_hour_cf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price Extra Hour CF (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tax_percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax (%)</FormLabel>
                  <FormControl>
                    <Input type="number" step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hcte_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HCTE Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default TourForm;
